import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchStoreProducts, resolveCategoryIds } from '../Api/storeApi';
import { getAllProducts } from '../Api/products';

/* ===========================================================================
   One implementation of "show a paged list of products".

   PERF-3 + PERF-1, and they had to be done together because the second is the
   reason the first is worth doing.

   WHAT THIS REPLACES. Six listing pages each carried their own copy of:

       const all = await getAllProducts();              // all 2,279 products
       const filtered = all.filter(p =>                 // keep the matching few
           p.categories?.some(c => c.slug === '...'));
       const page = filtered.slice(start, start + 30);  // show 30

   So rendering 30 products downloaded 2,279 and discarded the rest, and the
   same twenty-odd lines existed six times over — which is how they drifted
   apart (different slugs, one with two slugs, one with a stray duplicated
   effect, and one filtering on a category that does not exist).

   WHAT IT DOES INSTEAD. Asks the server for the page it needs:
   `?category=<ids>&page=<n>&per_page=<n>`. The Store API supports
   comma-separated category ids — verified against the live catalogue: 44 gives
   277 products, 73 gives 115, and 44,73 gives 392 — so multi-category pages
   work server-side too.

   TWO MODES, because one page cannot have both. There is no prefix/"starts
   with" filter in the Store API — `search` is a substring match, which answers a
   different question — so the A–Z filter on the all-products pages genuinely
   cannot be pushed to the server.

     server mode (default)   one request for the page being shown. Sorting goes
                             with it: `orderby=price&order=asc|desc`, verified
                             working against the live catalogue.

     client mode (namePrefix set)
                             falls back to the full catalogue, which is already
                             cached in IndexedDB, then filters, sorts and slices
                             locally — the only way to answer "names starting
                             with M".

   So the common case (no letter picked) gets the fast path, and the full
   download happens only when someone actually uses the A–Z filter. That is the
   whole gain: it is not that the full fetch disappears, it is that it stops
   being on the default path.
   =========================================================================== */

const DEFAULT_PER_PAGE = 30;

// Maps the pages' sort values onto Store API parameters. Anything else means
// "catalogue default order", which is what these pages did before.
const SORT_PARAMS = {
    lowToHigh: { orderby: 'price', order: 'asc' },
    highToLow: { orderby: 'price', order: 'desc' },
};

const sortLocally = (list, sort) => {
    if (sort === 'lowToHigh') return [...list].sort((a, b) => Number(a.price) - Number(b.price));
    if (sort === 'highToLow') return [...list].sort((a, b) => Number(b.price) - Number(a.price));
    return list;
};

/**
 * @param {object}   options
 * @param {string[]} options.categorySlugs  Category slugs to filter by. Empty =
 *                                          the whole catalogue, paged.
 * @param {number}   options.perPage
 *
 * @returns {{
 *   products: object[], loading: boolean, error: string|null,
 *   missingCategories: string[], total: number,
 *   currentPage: number, totalPages: number, setPage: (n:number)=>void
 * }}
 *
 * `missingCategories` is surfaced rather than swallowed: a slug that matches no
 * category is a configuration error, and rendering an empty grid for it looks
 * identical to "nothing in stock". That distinction is the whole reason
 * `/surgical` could sit broken without anyone noticing.
 */
export function useProductListing({
    categorySlugs = [],
    perPage = DEFAULT_PER_PAGE,
    // Passed straight to the Store API when set (e.g. 'popularity', 'date',
    // 'price', 'rating'). Left undefined means the catalogue's default order,
    // which is what every page did before this hook existed.
    orderby,
    order,
    // 'lowToHigh' | 'highToLow' | anything else. Applied server-side in server
    // mode and locally in client mode, so the two agree.
    sort,
    // A single letter. When set, switches to client mode — see the header.
    namePrefix = '',
} = {}) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [missingCategories, setMissingCategories] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Stable key so a fresh array literal in a caller's render does not restart
    // the effect on every render.
    const slugKey = useMemo(
        () => (categorySlugs || []).filter(Boolean).join(','),
        [categorySlugs]
    );

    // Guards against a slow page-1 response landing after a fast page-2 one and
    // overwriting it. The old per-page copies had no such guard.
    const requestRef = useRef(0);

    useEffect(() => {
        const requestId = ++requestRef.current;
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);

            try {
                const slugs = slugKey ? slugKey.split(',') : [];
                const { ids, missing } = await resolveCategoryIds(slugs);
                if (cancelled || requestId !== requestRef.current) return;

                setMissingCategories(missing);

                // Every requested category is unknown: there is no meaningful
                // query to run, and returning the whole catalogue instead would
                // be worse than returning nothing.
                if (slugs.length > 0 && ids.length === 0) {
                    setProducts([]);
                    setTotal(0);
                    setTotalPages(1);
                    return;
                }

                // --- client mode: a prefix filter the API cannot express ---
                if (namePrefix) {
                    const all = await getAllProducts();
                    if (cancelled || requestId !== requestRef.current) return;

                    const needle = namePrefix.toLowerCase();
                    let matched = (Array.isArray(all) ? all : []).filter((p) =>
                        p?.name?.trim().toLowerCase().startsWith(needle)
                    );

                    if (ids.length > 0) {
                        const idSet = new Set(ids);
                        matched = matched.filter((p) =>
                            (p.categories || []).some((c) => idSet.has(c.id))
                        );
                    }

                    matched = sortLocally(matched, sort);

                    const start = (currentPage - 1) * perPage;
                    setProducts(matched.slice(start, start + perPage));
                    setTotal(matched.length);
                    setTotalPages(Math.max(Math.ceil(matched.length / perPage), 1));
                    return;
                }

                // --- server mode: ask for exactly the page being shown ---
                const params = { page: currentPage, per_page: perPage };
                if (ids.length > 0) params.category = ids.join(',');

                const sortParams = SORT_PARAMS[sort];
                if (sortParams) Object.assign(params, sortParams);
                if (orderby) params.orderby = orderby;
                if (order) params.order = order;

                const result = await fetchStoreProducts(params);
                if (cancelled || requestId !== requestRef.current) return;

                setProducts(result.data);
                setTotal(result.total);
                setTotalPages(Math.max(result.totalPages || 1, 1));
            } catch (err) {
                if (cancelled || requestId !== requestRef.current) return;
                console.error('Product listing fetch failed:', err);
                setError(err?.message || 'Could not load products.');
                setProducts([]);
            } finally {
                if (!cancelled && requestId === requestRef.current) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [slugKey, currentPage, perPage, orderby, order, sort, namePrefix]);

    // Reset to page 1 whenever the result set changes shape, or page 4 of the old
    // list gets requested against the new one. The previous per-page copies
    // handled this inconsistently: some reset on filter change, and one tried to
    // correct an overflowing page *after* rendering it.
    useEffect(() => {
        setCurrentPage(1);
    }, [slugKey, namePrefix, sort]);

    const setPage = useCallback((next) => {
        const n = Number(next);
        if (Number.isFinite(n) && n >= 1) setCurrentPage(n);
    }, []);

    return {
        products,
        loading,
        error,
        missingCategories,
        total,
        currentPage,
        totalPages,
        setPage,
    };
}

export default useProductListing;
